import React from "react";

import {
    Svg as LabUISvg,
    Props,
    SvgElement,
    svgNames as labuiSvgNames,
} from "@thelabnyc/thelabui/src/components/Svg";

import colors from "../../global-styles/color-variables.module.scss";

/**
 * thelab-ui provides some icons that might be useful, but you'll
 * definitely need project-specific icons. Put the names of those
 * icons here.
 */
export const svgNames = [
    "arrow",
    "burger",
    "car",
    "caret",
    "caret-fill",
    "caret-vertical",
    "check",
    "circle-alert",
    "circle-info",
    "circle-question",
    "download",
    "external",
    "logo-chevron-only",
    "logo-horizontal",
    "minus",
    "pause",
    "pin-fill",
    "plus",
    "quote",
    "reload",
    "search",
    "share",
    "slides",
    "video-pause-fill",
    "video-play-fill",
    "logo-vertical",
    "minus",
    "pause",
    "pin-fill",
    "plus",
    "quote",
    "search",
    "share",
    "slides",
    "video-pause-fill",
    "video-play-fill",
    "x",
] as const;

/** If you wish to exclude thelab-ui icons entirely, add their names here. */
const labuiBlacklist = [""];
const labuiSvgNamesFiltered = labuiSvgNames.filter(
    (name) => !labuiBlacklist.includes(name),
);

/**
 * Project-specific SVGs plus SVGs from thelab-ui.
 *
 * This deduplicates, so if you put something into svgNames that's in
 * labuiSvgNames, it'll only show up once. This is useful if you want
 * to override something from thelab-ui.
 */
export const allSvgNames = [
    ...new Set([...labuiSvgNamesFiltered, ...svgNames].sort()),
] as const;

export type SVGName = (typeof allSvgNames)[number];

/**
 * This repo's solution for using icon-like SVGs. "Icon-like" means that the SVG
 * is a relatively small and simple graphic. Most, if not all of our icons are
 * monochromatic, their fills are modified with CSS, and they might be reused in
 * different contexts around the site.
 *
 * This is building around a lab-ui base Svg component, which means that some of
 * these icons are not specific to this repository and might not be what we want
 * to use. They can be overridden if necessary.
 *
 * To add an SVG:
 * - Add a name to `svgNames`
 * - Make the SVG TSX-friendly. https://react-svgr.com/playground/?typescript=true
 *   can help with this
 * - Add a case with your name and your element, replacing `<svg>` with `<SvgElement>`.
 *   You should only need `{...props}` and the `viewBox` attribute; height/width and
 *   fill color can be set with CSS
 *
 * You can hardcode fill colors, but you'll need to be very sure that the color will
 * not change. Typically the logo is a place where you'd do this.
 *
 * Some images might be in the SVG format but not fit this mold, and can be treated as
 * static images instead (placed in the static folder and loaded with `<img>`).
 */
export const Svg = ({ name, ...props }: Props<SVGName>) => {
    switch (name) {
        case "arrow": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="m22.25 11.94-9.59-9.61-.71.71 8.52 8.53H1.7v1H20.47l-8.52 8.53.71.71 9.59-9.61v-.26z" />
                </SvgElement>
            );
        }
        case "burger": {
            //16, 30
            return (
                <SvgElement {...props} viewBox="0 0 48 48">
                    <rect y="23" width="48" height="2" className="topBar" />
                    <rect y="23" width="48" height="2" className="bottomBar" />
                </SvgElement>
            );
        }
        case "car": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path
                        d="M23.62 8.14h-2.1l-1.73-5.4c-.15-.52-.67-.9-1.27-.9H5.47c-.6 0-1.12.38-1.28.9l-1.72 5.4H.38c-.22 0-.38.15-.38.38v1.12c0 .23.15.38.38.38h1.2c-.38.38-.75 1.72-.75 2.17v9.07c0 .53.38.9.9.9h3.52c.53 0 .9-.38.9-.9v-1.95h11.7v1.95c0 .53.38.9.9.9h3.52c.52 0 .9-.38.9-.9v-9.75c0-.45-.3-1.12-.75-1.5h1.2c.23 0 .38-.15.38-.38V8.51c0-.22-.15-.38-.38-.38ZM5.09 16.02c-.22.23-.53.3-.82.3s-.6-.15-.83-.3c-.23-.22-.3-.52-.3-.82s.15-.6.3-.82c.22-.22.52-.3.83-.3s.6.15.82.38c.22.23.3.53.3.83.07.22-.08.52-.3.75Zm.15-7.88c-.38 0-.6-.38-.53-.67l1.05-3.53c.15-.45.53-.75.97-.75h10.5c.45 0 .83.3.98.75l1.05 3.53c.08.38-.15.67-.52.67H5.25Zm15.23 7.88c-.23.23-.52.3-.83.3s-.6-.15-.82-.3c-.23-.22-.3-.52-.3-.82s.15-.6.38-.82c.23-.22.52-.3.83-.3s.6.15.82.38c.23.23.3.53.3.83 0 .22-.15.52-.38.75Z"
                        style={{ fillRule: "evenodd" }}
                    />
                </SvgElement>
            );
        }
        case "caret": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="m8.25 20.49-.71-.71 7.63-7.63-7.66-7.66.71-.71 8.27 8.24v.23l-8.24 8.24Z" />
                </SvgElement>
            );
        }
        case "caret-fill": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="M12 16 5 8h14l-7 8Z" />
                </SvgElement>
            );
        }
        case "caret-vertical": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <polygon points="2.9 15.7 4.4 17.1 12 9.4 19.6 17.1 21.1 15.6 12.3 6.9 11.7 6.9 2.9 15.7" />
                </SvgElement>
            );
        }
        case "check": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="M6.8 21.4.16 14.76l.71-.71 6.01 6.01L22.79 4.15l.71.71L7.23 21.13l-.27.27h-.15Z" />
                </SvgElement>
            );
        }
        case "circle-alert": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="M12 0C5.38 0 0 5.38 0 12s5.38 12 12 12 12-5.38 12-12S18.62 0 12 0Zm0 23C5.93 23 1 18.07 1 12S5.93 1 12 1s11 4.93 11 11-4.93 11-11 11Zm-.49-7.24h1V18h-1v-2.24Zm.99-1.13h-1V6h1v8.63Z" />
                </SvgElement>
            );
        }
        case "circle-info": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="M12 24C5.38 24 0 18.62 0 12S5.38 0 12 0s12 5.38 12 12-5.38 12-12 12Zm0-23C5.93 1 1 5.93 1 12s4.93 11 11 11 11-4.93 11-11S18.07 1 12 1Zm.5 5h-1v2.24h1V6Zm.01 3.37h-1V18h1V9.37Z" />
                </SvgElement>
            );
        }
        case "circle-question": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="M12 0C5.38 0 0 5.38 0 12s5.38 12 12 12 12-5.38 12-12S18.62 0 12 0Zm0 23C5.93 23 1 18.07 1 12S5.93 1 12 1s11 4.93 11 11-4.93 11-11 11Zm-.65-7.24h1V18h-1v-2.24Zm3.28-9.36c.58.59.87 1.32.87 2.19 0 .64-.15 1.25-.44 1.81-.4.76-.98 1.29-1.55 1.8-.23.2-.46.41-.67.63-.51.53-.51.55-.51 1.59v.21h-1v-.21c0-1.19.03-1.5.79-2.29.23-.24.47-.46.72-.68.52-.47 1.01-.91 1.33-1.52.21-.42.32-.87.32-1.35 0-.6-.19-1.09-.58-1.49-.4-.4-1.04-.61-1.92-.61s-1.58.24-1.92.67c-.38.48-.58 1.22-.58 2.2h-1c0-1.23.26-2.15.79-2.82.54-.68 1.48-1.05 2.71-1.05 1.15 0 2.04.3 2.63.9Z" />
                </SvgElement>
            );
        }
        case "download": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="M1 23v1h22v-1H1Zm11.13-2.45 9.61-9.59-.71-.71-8.53 8.52V0h-1v18.77l-8.53-8.52-.71.71 9.61 9.59h.26Z" />
                </SvgElement>
            );
        }
        case "external": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="M23.99.2 23.8.01 12.23 0v1l10.05.01L9.07 14.23l.7.71 7.2-7.2 6.02-6.02.01 10.05h1L23.99.2z" />
                    <path d="M21.5 23H1V2.5h7v-1H0V24h22.5v-8h-1v7z" />
                </SvgElement>
            );
        }
        case "logo-chevron-only": {
            return (
                <SvgElement {...props} viewBox="0 0 126 81">
                    <path
                        d="M62.4881 0.5L0 47.199V80.5H33.0009L63 58.0773L93.004 80.5H126V47.199L63.5169 0.5H62.4881Z"
                        fill={colors.green}
                    />
                </SvgElement>
            );
        }
        case "logo-horizontal": {
            return (
                <SvgElement {...props} viewBox="0 0 540.47 102.02">
                    <path d="M540.47 11.17v68.75l-1.4 1.39h-4.74l-1.39-1.39V11.17l1.39-1.39h4.74l1.4 1.39ZM248.45 26.72l-1.4 1.39v29.11c0 8.53-2.87 18.7-16.56 18.7-8.03 0-12.28-4.99-12.28-14.42V28.12l-1.39-1.39h-4.74l-1.39 1.39v33.39c0 13.27 6.83 20.88 18.74 20.88 7.56 0 14.06-3.2 17.42-8.56h.29l1.02 6.13 1.36 1.36h3.67l1.39-1.39V28.12l-1.39-1.39h-4.74ZM315 54.02c0 17.76-8.6 28.37-23.02 28.37-11.55 0-16.03-5.9-17.45-8.56h-.18v26.8l-1.4 1.39h-4.74l-1.39-1.39V28.12l1.39-1.39h3.67l1.36 1.36 1.02 6.13h.26c1.43-2.65 5.9-8.56 17.45-8.56 14.41 0 23.02 10.6 23.02 28.36h.01Zm-7.53 0c0-14.13-5.88-21.91-16.56-21.91s-16.56 7.78-16.56 21.91 5.88 21.91 16.56 21.91 16.56-7.78 16.56-21.91Zm63.7-2.14v3.44l-1.39 1.39h-38.51l-.74.74c.05 3.03 1.06 7.61 3.78 11.49 2.23 3.19 6.44 6.98 13.85 6.98s12.1-3.28 14.45-10.32l.11-.18 1.26-1.27h4.74l1.43 1.43-.05.28c-.46 2.76-3.68 16.51-21.94 16.51S323 67.72 323 54v-2.14c0-15.93 9.45-26.22 24.09-26.22s24.09 10.29 24.09 26.22v.02Zm-7.53-2.37c-.11-13.73-10.46-17.4-16.56-17.4s-16.45 3.67-16.56 17.4l.74.74h31.63l.74-.74h.01Zm42.44-22.79c-7.04 0-12.18 2.52-15.28 7.49h-.29l-1.02-6.13-1.36-1.36h-3.67l-1.39 1.39v51.8l1.39 1.39h4.74l1.39-1.39V50.8c0-8.04 2.5-17.63 14.42-17.63h5.58l1.39-1.39v-3.67l-1.39-1.39h-4.51Zm38.94-1.07c-7.56 0-14.06 3.2-17.42 8.56h-.29l-1.02-6.13-1.36-1.36h-3.67l-1.4 1.39v51.8l1.4 1.39H426l1.4-1.39V50.8c0-8.53 2.87-18.7 16.56-18.7 8.61 0 13.35 5.5 13.35 15.49v32.32l1.39 1.39h4.74l1.39-1.39V47.59c0-13.74-7.4-21.95-19.81-21.95Zm73.68 19.81v34.46l-1.39 1.39h-3.67l-1.35-1.35-1.01-5.07h-.32c-3.49 5.04-9.18 7.49-17.41 7.49-11.73 0-18.74-6.21-18.74-16.6 0-11.56 7.96-17.67 23.02-17.67 5.64 0 11.33.83 12.64 1.03l.71-.71v-2.98c0-9.86-3.49-13.35-13.35-13.35-11.62 0-13.35 6.35-13.35 10.14v.23l-1.39 1.4h-4.74l-1.4-1.4v-.23c0-12.25 11.25-16.6 20.88-16.6 14.63 0 20.88 5.92 20.88 19.81h-.01Zm-7.53 10.93-.79-.79c-.78-.14-5.77-1.03-11.5-1.03-11.61 0-16.56 3.35-16.56 11.21 0 6.92 3.9 10.14 12.28 10.14s16.56-5.32 16.56-15.49v-4.05h.01Zm-325.71-6.12-.87-.17c-10.19-2.03-15.8-3.15-15.8-9.98 0-5.09 4.09-8 11.21-8 8.15 0 12.28 3.05 12.28 9.07v.23l1.39 1.39h4.74l1.39-1.39v-.23c0-9.87-7.22-15.53-19.81-15.53-9.02 0-18.74 4.52-18.74 14.46 0 8.19 5.58 13.12 17.57 15.52 10.71 2.14 15.59 3.12 15.59 11.22 0 6.11-4.37 9.07-13.35 9.07-8.36 0-13.35-4.19-13.35-11.21v-.23l-1.39-1.39h-4.74l-1.39 1.39v.23c0 10.57 8.39 17.67 20.88 17.67s20.88-5.81 20.88-15.53-4.78-14.25-16.49-16.59Z" />
                    <path
                        d="m64.23 0 63.19 47.23v33.68H94.05L63.71 58.23 33.37 80.91H0V47.23L63.19 0h1.04Z"
                        fill={colors.green}
                    />
                </SvgElement>
            );
        }
        case "logo-vertical": {
            return (
                <SvgElement {...props} viewBox="0 0 127 131">
                    <path d="M127 100.47v23.1l-.47.47h-1.58l-.47-.47v-23.1l.47-.47h1.58l.47.47Zm-97.53 5.23-.47.47v9.78c0 2.86-.96 6.28-5.53 6.28-2.68 0-4.1-1.68-4.1-4.85v-11.22l-.47-.47h-1.58l-.47.47v11.22c0 4.46 2.28 7.02 6.26 7.02 2.52 0 4.7-1.07 5.82-2.88h.1l.34 2.06.45.45h1.23l.47-.47v-17.41l-.47-.47h-1.58Zm22.23 9.17c0 5.97-2.87 9.53-7.69 9.53-3.86 0-5.35-1.99-5.83-2.88h-.06v9l-.47.47h-1.58l-.47-.47v-24.37l.47-.47h1.23l.45.45.34 2.06h.09c.48-.89 1.97-2.87 5.83-2.87 4.81 0 7.69 3.56 7.69 9.53Zm-2.51 0c0-4.75-1.96-7.36-5.53-7.36s-5.53 2.61-5.53 7.36 1.96 7.36 5.53 7.36 5.53-2.62 5.53-7.36Zm21.28-.72v1.15l-.47.47H57.14l-.25.25c.01 1.02.35 2.56 1.26 3.86.74 1.07 2.15 2.35 4.63 2.35s4.04-1.1 4.83-3.47l.03-.06.42-.42h1.58l.48.48v.1c-.17.93-1.25 5.55-7.34 5.55s-8.4-4.93-8.4-9.53v-.72c0-5.36 3.16-8.81 8.05-8.81s8.05 3.46 8.05 8.81Zm-2.51-.79c-.04-4.62-3.49-5.85-5.53-5.85s-5.49 1.23-5.53 5.85l.25.25h10.56l.25-.25Zm14.18-7.66c-2.35 0-4.07.85-5.1 2.51h-.1l-.34-2.06-.45-.45h-1.23l-.47.47v17.41l.47.47h1.58l.47-.47v-9.78c0-2.71.83-5.92 4.82-5.92h1.86l.47-.47v-1.23l-.47-.47h-1.51Zm13.01-.36c-2.52 0-4.7 1.07-5.82 2.87h-.1l-.34-2.06-.45-.45h-1.23l-.47.47v17.41l.47.47h1.58l.47-.47v-9.78c0-2.86.96-6.28 5.53-6.28 2.88 0 4.46 1.85 4.46 5.21v10.86l.47.47h1.58l.47-.47v-10.86c0-4.62-2.48-7.38-6.62-7.38Zm24.61 6.66v11.58l-.47.47h-1.22l-.45-.45-.34-1.7h-.11c-1.17 1.69-3.07 2.52-5.81 2.52-3.92 0-6.26-2.09-6.26-5.58 0-3.88 2.66-5.94 7.69-5.94 1.88 0 3.78.28 4.22.35l.24-.24v-1c0-3.31-1.17-4.49-4.46-4.49-3.88 0-4.46 2.14-4.46 3.41v.07l-.47.47h-1.58l-.47-.47v-.07c0-4.12 3.75-5.58 6.97-5.58 4.89 0 6.97 1.99 6.97 6.66Zm-2.51 3.67-.26-.26c-.26-.05-1.93-.34-3.84-.34-3.88 0-5.53 1.13-5.53 3.77 0 2.32 1.3 3.41 4.1 3.41s5.53-1.79 5.53-5.21v-1.36ZM8.47 113.61l-.29-.06c-3.4-.68-5.28-1.06-5.28-3.35 0-1.71 1.36-2.69 3.74-2.69 2.72 0 4.1 1.03 4.1 3.05v.08l.47.47h1.58l.47-.47v-.08c0-3.32-2.41-5.22-6.62-5.22-3.02 0-6.26 1.52-6.26 4.86 0 2.75 1.86 4.41 5.87 5.22 3.58.72 5.21 1.05 5.21 3.77 0 2.05-1.46 3.05-4.46 3.05-2.79 0-4.46-1.41-4.46-3.77v-.07l-.47-.47H.47l-.47.47v.07c0 3.55 2.8 5.94 6.97 5.94s6.97-1.95 6.97-5.22-1.6-4.79-5.51-5.58Z" />
                    <path
                        d="M62.49 0 0 46.7V80h33l30-22.42L93 80h33V46.7L63.52 0h-1.03Z"
                        fill={colors.green}
                    />
                </SvgElement>
            );
        }
        case "minus": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="M24 12.5H0v-1h24v1Z" />
                </SvgElement>
            );
        }
        case "pause": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="M8.5 20h-1V4h1v16Zm9-16h-1v16h1V4Z" />
                </SvgElement>
            );
        }
        case "pin-fill": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path
                        d="M20.18 8.11C20.18 3.64 16.51 0 12 0S3.82 3.64 3.82 8.11c0 3.21 1.35 5.24 3.03 7.76.39.59.81 1.21 1.22 1.88L12 24.01l3.93-6.26c.42-.67.83-1.29 1.22-1.88 1.68-2.52 3.03-4.56 3.03-7.76ZM12 12.18c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4Z"
                        style={{ fillRule: "evenodd" }}
                    />
                </SvgElement>
            );
        }
        case "plus": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="M24 12.5H12.5V24h-1V12.5H0v-1h11.5V0h1v11.5H24v1Z" />
                </SvgElement>
            );
        }
        case "quote": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="m24 3.29-4.77 7.13v.79l.8.79h2.39l.8.79v7.92l-.8.79h-7.95l-.8-.79V12l.8-2.38 4.77-6.33 1.59-.79h3.18v.79Zm-13.66 0-4.77 7.13v.79l.8.79h2.39l.8.79v7.92l-.8.79H.8l-.8-.79V12l.8-2.38 4.77-6.33 1.59-.79h3.18v.79Z" />
                </SvgElement>
            );
        }
        case "reload": {
            return (
                <SvgElement {...props} viewBox="0 0 16 18">
                    <path d="m13 4.9-.7.7c1.2 1.2 1.8 2.7 1.8 4.4 0 3.4-2.8 6.2-6.2 6.2-3.4 0-6.2-2.8-6.2-6.2 0-3.3 2.6-6 5.9-6.2L6.4 5.1l.7.7 2.4-2.4v-.2L7.1.9l-.7.7 1.3 1.3C3.9 3 .8 6.1.8 10c0 4 3.2 7.2 7.2 7.2s7.2-3.2 7.2-7.2c0-1.9-.8-3.8-2.2-5.1z" />
                </SvgElement>
            );
        }
        case "search": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="m23.86 23.15-6.31-6.31c1.61-1.78 2.6-4.12 2.6-6.7-.01-5.51-4.49-10-10.01-10s-10 4.49-10 10 4.49 10 10 10c2.58 0 4.92-.99 6.7-2.6l6.31 6.31.71-.71Zm-13.72-4.01c-4.96 0-9-4.04-9-9s4.04-9 9-9 9 4.04 9 9-4.04 9-9 9Z" />
                </SvgElement>
            );
        }
        case "share": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="m21.04 2.8-.19-.18-13.57-.01v1l12.05.01L6.06 16.89l-3.75 3.75.71.71 11-11.01 6.02-6.02.01 12.06h1L21.04 2.8z" />
                </SvgElement>
            );
        }
        case "slides": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="M23.5 20.5h-3v-1h2v-18h-18v2h-1v-3h20v20ZM19 5H0v19h19V5ZM1 6h17v17H1V6Z" />
                </SvgElement>
            );
        }
        case "video-pause-fill": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path
                        d="M9 3H5v18h4V3Zm10 0h-4v18h4V3Z"
                        style={{ fillRule: "evenodd" }}
                    />
                </SvgElement>
            );
        }
        case "video-play-fill": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="m20.5 12-17 11V1l17 11Z" />
                </SvgElement>
            );
        }
        case "x": {
            return (
                <SvgElement {...props} viewBox="0 0 24 24">
                    <path d="m12.71 11.8 9.55 9.55-.71.71L12 12.51l-9.55 9.54-.71-.71 9.55-9.55-9.54-9.54.71-.71 9.55 9.55 9.54-9.54.71.71-9.55 9.55Z" />
                </SvgElement>
            );
        }
        default:
            return <LabUISvg name={name} {...props}></LabUISvg>;
    }
};
